import 'chartjs-adapter-date-fns'

import {
    ChartData,
    ChartDataset,
    ChartEvent,
    Chart as ChartJS,
    Tooltip as ChartJSTooltip,
    LineElement,
    LinearScale,
    Point,
    PointElement,
    Tick,
    TimeScale,
    TooltipModel,
} from 'chart.js'
import zoomPlugin from 'chartjs-plugin-zoom'
import { subDays, subHours } from 'date-fns'
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect'
import { Tooltip } from 'primereact/tooltip'
import React, { FC, HTMLAttributes, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Line } from 'react-chartjs-2'

import formatDate from '../../lib/formatDate'
import { InfoIcon } from '../../lib/icons/info-icon'
import { theme } from '../../lib/theme'
import useText from '../hooks/useText'

import GroupedButton from './GroupedButton'

ChartJS.register(zoomPlugin, LinearScale, TimeScale, PointElement, LineElement, ChartJSTooltip)

const colors = [
    theme.brandColors.CA_Blue,
    theme.brandColors.CA_Green,
    theme.brandColors.CA_Mid_Gray,
    theme.brandColors.CA_Yellow,
    theme.brandColors.CA_Coral,
    theme.brandColors.CA_Purple,
    theme.brandColors.CA_Error_Red,
]

type Props = {
    data: ChartData<'line'>
    yAxisLabel?: string
    xAxisLabel?: string
    aspectRatio?: number
    enableZoomShortcuts?: boolean
    maintainAspectRatio?: boolean
} & HTMLAttributes<HTMLDivElement>

const yOffset = 1

type LegendDataset = { label?: string; backgroundColor?: string; hidden: boolean }

type LegendProps = {
    datasets: LegendDataset[]
    onVisibilityChange: (event: MultiSelectChangeEvent) => void
} & HTMLAttributes<HTMLDivElement>

function Legend({ datasets, onVisibilityChange, ...rest }: LegendProps) {
    const value = datasets.filter((item) => !item.hidden).map((item) => item.label)
    const options = datasets.map((item) => item.label)

    const itemTemplate = (option: string) => {
        const item = datasets.find((item) => item.label === option)
        return item ? (
            <div className="flex align-items-center" data-testid={item.label}>
                <span
                    className="w-1rem h-1rem inline mr-1"
                    style={{ backgroundColor: item.backgroundColor || 'transparent' }}
                />
                <div>{option}</div>
            </div>
        ) : null
    }

    return (
        <MultiSelect
            {...rest}
            placeholder="Filter"
            value={value}
            options={options}
            onChange={onVisibilityChange}
            itemTemplate={itemTemplate}
            data-testid="legend-multiselect"
            className={'w-10 xl:w-7'}
        />
    )
}

// usage of spread operator on long arrays passed as parameter exceeds the maximum call stack size of the functions,
// like Math.max in Chrome, so I had to write a custom function
function arrayMax(arr: number[]): number {
    let len = arr.length
    let max = -Infinity
    while (len--) {
        if (arr[len] > max) {
            max = arr[len]
        }
    }
    return max
}

export const LineChart: FC<Props> = ({
    data,
    yAxisLabel,
    xAxisLabel,
    aspectRatio = 2,
    enableZoomShortcuts,
    className,
    ...rest
}) => {
    const text = useText()
    const maxY = useMemo(
        () =>
            arrayMax(
                data.datasets.map((dataset) =>
                    arrayMax(dataset.data.map((point) => (typeof point === 'number' ? point : point?.y || 0)))
                )
            ),
        [data.datasets]
    )

    const zoomLevels = useMemo(
        () => ({
            '24h': { min: subHours(new Date(), 24).getTime(), max: new Date().getTime() },
            '7d': { min: subDays(new Date(), 7).getTime(), max: new Date().getTime() },
            // Add more zoom levels as needed...
        }),
        []
    )

    const scales = useMemo(
        () => ({
            x: {
                position: 'bottom' as const,
                type: 'time' as const,
                ticks: {
                    autoSkip: true,
                    autoSkipPadding: 20,
                    maxRotation: 0,
                    color: theme.brandColors.CA_Black,
                    font: {
                        family: 'Lateral',
                        size: 12,
                        weight: 'normal' as const,
                        lineHeight: 1.2,
                    },
                    callback: function (value: number | string): string {
                        const date = new Date(value)
                        const hour = date.getHours()
                        const minute = date.getMinutes()
                        const second = date.getSeconds()

                        // This tick is a day
                        if (hour === 0 && minute === 0 && second === 0) {
                            return formatDate(date, 'MMM d')
                        }
                        // This tick is an hour
                        else if (minute === 0 && second === 0) {
                            return formatDate(date, 'HH:mm')
                        }
                        // This tick is a minute
                        else if (second === 0) {
                            return formatDate(date, 'HH:mm')
                        }
                        // This tick is a second
                        else {
                            return formatDate(date, 'HH:mm:ss')
                        }
                    },
                },
                time: {
                    bounds: 'data' as const,
                    displayFormats: {
                        hour: 'HH:mm',
                        minute: 'HH:mm',
                        second: 'HH:mm:ss',
                        millisecond: 'HH:mm:ss',
                        day: 'd eee',
                    },
                },
                title: {
                    display: xAxisLabel !== undefined,
                    text: xAxisLabel,
                    color: theme.brandColors.CA_Black,
                    font: {
                        family: 'Lateral',
                        size: 14,
                        weight: 'normal' as const,
                        lineHeight: 1.2,
                    },
                },
            },
            y: {
                max: maxY + yOffset,
                position: 'left' as const,
                ticks: {
                    callback: (val: number | string, index: number, ticks: Tick[]) => {
                        if (index === 0 || index === ticks.length - 1) return null
                        if (typeof val !== 'number') return val
                        return Number.isInteger(val) ? val : val.toFixed(3)
                    },
                    color: theme.brandColors.CA_Black,
                    font: {
                        family: 'Lateral',
                        size: 12,
                        weight: 'normal' as const,
                        lineHeight: 1.2,
                    },
                },
                grid: {
                    color: theme.brandColors.CA_Gray,
                },
                title: {
                    display: yAxisLabel !== undefined,
                    text: yAxisLabel,
                    color: theme.brandColors.CA_Black,
                    font: {
                        family: 'Lateral',
                        size: 14,
                        weight: 'normal' as const,
                        lineHeight: 1.2,
                    },
                },
            },
        }),
        [maxY, xAxisLabel, yAxisLabel]
    )

    const [currentZoomLevel, setCurrentZoomLevel] = useState<keyof typeof zoomLevels | null>(null)

    const chartRef = useRef<ChartJS<'line', Point[]>>(null)
    const legendRef = useRef<HTMLDivElement>(null)

    // Set the initial zoom level after the component has mounted
    useLayoutEffect(() => {
        if (!enableZoomShortcuts) return
        setTimeout(() => {
            setCurrentZoomLevel('24h')
        }, 0)
    }, [enableZoomShortcuts])

    useEffect(() => {
        if (chartRef.current && currentZoomLevel !== null) {
            const chart = chartRef.current
            const zoomLevel = zoomLevels[currentZoomLevel]
            chart.zoomScale('x', zoomLevel, 'zoom')

            // Update the chart
            chart.update()
        }
    }, [currentZoomLevel, zoomLevels])

    const zoomOptions = useMemo(
        () => ({
            pan: {
                enabled: true,
                mode: 'xy' as const,
                scaleMode: 'xy' as const,
                wheel: {
                    enabled: false, // Disable panning with mouse wheel
                },
                modifierKey: 'alt' as const,
            },
            zoom: {
                wheel: {
                    enabled: true,
                },
                pinch: {
                    enabled: true,
                },
                drag: {
                    enabled: true,
                    borderColor: theme.brandColors.CA_Mid_Blue,
                    borderWidth: 1,
                    backgroundColor: theme.brandColors.CA_Light_Blue,
                    threshold: 10,
                },
                mode: 'x' as const,
                scaleMode: 'x' as const,
            },
            limits: {
                x: {
                    minRange: 10 * 60 * 1000, // 10 minutes in milliseconds
                    min: 'original' as const,
                    max: 'original' as const,
                },
                y: {
                    minRange: 10,
                    min: 'original' as const,
                    max: maxY + yOffset,
                },
            },
        }),
        [maxY, zoomLevels]
    )

    const coloredData = useMemo(
        () => ({
            ...data,
            datasets: data.datasets.map((dataset, index) => ({
                borderColor: colors[index % colors.length], // Use modulo to cycle through colors
                backgroundColor: colors[index % colors.length],
                borderWidth: 1,
                ...dataset,
            })),
        }),
        [data]
    )

    useLayoutEffect(() => {
        if (legendRef.current) {
            legendRef.current.innerHTML = '' // Clear the legend

            const title = document.createElement('div')
            // Generate legend items
            const bodyItems = coloredData.datasets.map((dataset) => {
                const legendItem = document.createElement('div')
                const legendColor = document.createElement('span')
                legendColor.setAttribute(
                    'style',
                    'display: inline-block; width: 10px; height: 10px; background-color: ' + dataset.backgroundColor
                )
                legendItem.append(legendColor)
                const textNode = document.createTextNode(` ${dataset.label}`)
                legendItem.appendChild(textNode)
                return legendItem
            })

            const body = document.createElement('div')

            // Calculate the width of the items based on the parent's width and the number of items
            const itemWidth = Math.floor(legendRef.current.width / Math.ceil(bodyItems.length / 3))

            bodyItems.forEach((item) => {
                item.style.width = `${itemWidth}px`
            })

            body.setAttribute('style', 'display: flex; flex-direction: column; flex-wrap: wrap; height: 60px;')
            body.append(...bodyItems)

            legendRef.current.append(title, body)
        }
    }, [coloredData])

    const options = useMemo(
        () => ({
            normalized: true,
            parsing: false as const,
            interaction: {
                mode: 'index' as const,
                intersect: false,
            },
            tension: false,
            stepped: 0,
            borderDash: [],
            spanGaps: true,
            animation: false as const,
            maintainAspectRatio: false,
            responsive: true,
            aspectRatio,
            plugins: {
                zoom: zoomOptions,
                decimation: {
                    enabled: false,
                },
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                    external: function (context: { chart: ChartJS<'line'>; tooltip: TooltipModel<'line'> }) {
                        // Update the custom legend with the data from the hovered dataset
                        if (legendRef.current === null) return

                        const tooltipModel = context.tooltip
                        if (tooltipModel.opacity === 0) {
                            return
                        }
                        const dataPoint = tooltipModel.dataPoints[0]

                        if (!dataPoint) return

                        const datasets = context.chart.data.datasets as ChartDataset<'line', Point[]>[]
                        const label = new Date(datasets[0].data[dataPoint!.dataIndex].x).toLocaleString('sv-SE')
                        const legend = legendRef.current.children

                        const title = legend[0]
                        title.innerHTML = label
                        title.classList.add('mb-4', 'font-medium')
                        const body = legend[1]

                        for (let i = 0; i < context.chart.data.datasets.length; i++) {
                            const datasetLabel = context.chart.data.datasets[i].label
                            const value = datasets[i].data[dataPoint.dataIndex].y

                            const legendItem = body.children[i]
                            legendItem.innerHTML = '' // Clear the content of the legend item

                            const legendColor = document.createElement('span')
                            legendColor.textContent = ' '
                            legendColor.setAttribute(
                                'style',
                                'display: inline-block; width: 10px; height: 10px; background-color: ' +
                                    datasets[i].backgroundColor
                            )
                            legendItem.append(legendColor)
                            const textNode = document.createTextNode(` ${datasetLabel}: ${isNaN(value) ? '-' : value}`)
                            legendItem.appendChild(textNode)
                        }
                    },
                },
            },

            scales,

            datasets: {
                line: {
                    pointRadius: 0, // disable for all `'line'` datasets
                },
            },
            elements: {
                point: {
                    radius: 0, // default to disabled in all datasets
                },
            },
        }),
        [scales, zoomOptions]
    )

    const [datasets, setDatasets] = useState<{ label?: string; backgroundColor: string; hidden: boolean }[]>([])

    useEffect(() => {
        if (chartRef.current) {
            const chart = chartRef.current
            setDatasets(
                chart.data.datasets.map((dataset, index) => ({
                    label: dataset.label,
                    backgroundColor: dataset.backgroundColor as string,
                    hidden: chart.getDatasetMeta(index).hidden,
                }))
            )
        }
    }, [data.datasets])

    return (
        <div className={className}>
            <div className="flex justify-content-between align-items-center mb-4">
                <Legend
                    {...rest}
                    datasets={datasets}
                    onVisibilityChange={(event: MultiSelectChangeEvent) => {
                        const chart = chartRef.current
                        if (chart === null) return

                        if (Array.isArray(event.selectedOption)) {
                            const selectAll = event.selectedOption.length === datasets.length
                            datasets.forEach((_, index) => {
                                chart.setDatasetVisibility(index, selectAll)
                            })
                            chart.update()
                            setDatasets(
                                chart.data.datasets.map((dataset, index) => ({
                                    label: dataset.label,
                                    backgroundColor: dataset.backgroundColor as string,
                                    hidden: chart.getDatasetMeta(index).hidden,
                                }))
                            )
                            return
                        }
                        const index = datasets.findIndex((item) => event.selectedOption === item.label)
                        chart.setDatasetVisibility(index, !chart.isDatasetVisible(index))
                        chart.update()
                        setDatasets(
                            chart.data.datasets.map((dataset, index) => ({
                                label: dataset.label,
                                backgroundColor: dataset.backgroundColor as string,
                                hidden: chart.getDatasetMeta(index).hidden,
                            }))
                        )
                    }}
                />
                <Tooltip target="#line-chart-tooltip" />
                <span
                    id="line-chart-tooltip"
                    className="cursor-pointer z-1"
                    data-pr-tooltip={text('msgLineChartHelpText')}
                    data-pr-position="top"
                    data-pr-at="right+8 top-5"
                >
                    <InfoIcon size={20} />
                </span>
            </div>
            <div className="relative h-30rem">
                <Line
                    key="line-chart"
                    ref={chartRef}
                    data={coloredData}
                    options={options}
                    data-testid="line-chart"
                    plugins={[
                        {
                            id: 'customEventListener',
                            afterEvent: (chart: ChartJS, evt: { event: ChartEvent }) => {
                                if (evt.event.type !== 'click') {
                                    return
                                }
                                // zoom out on double click
                                if ((evt.event.native as MouseEvent)?.detail === 2) {
                                    chart.resetZoom()
                                }
                            },
                        },
                    ]}
                />
            </div>
            <div className="pb-4" ref={legendRef}></div>
            {enableZoomShortcuts ? (
                <GroupedButton<keyof typeof zoomLevels | null>
                    value={currentZoomLevel}
                    options={[
                        {
                            label: text('lbl24h'),
                            value: '24h',
                        },
                        {
                            label: text('lbl7days'),
                            value: '7d',
                        },
                    ]}
                    onChange={(value) => setCurrentZoomLevel(value)}
                />
            ) : null}
        </div>
    )
}
